import { Sodo } from '../shared/interfaces';

/** Normalize POST/PUT /Soda response to an array of raw API soda rows. */
export function normalizeSodaApiListResponse(response: unknown): unknown[] {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== 'object') return [];
  const r = response as Record<string, unknown>;
  if (Array.isArray(r.data)) return r.data;
  if (r.data && typeof r.data === 'object') return [r.data];
  if (r.id != null || r.Id != null || r.sodaId != null) return [response];
  return [];
}

/** IDs to remove when deleting a main or partner soda (parent + children). */
export function collectRelatedSodaIdsForDelete(currentSodas: Sodo[], deletedSoda: Sodo): number[] {
  const deletedIdNum = Number(deletedSoda.id);
  const ids = new Set<number>([deletedIdNum]);
  if (deletedSoda.parentId != null) {
    ids.add(Number(deletedSoda.parentId));
  }
  currentSodas
    .filter((s) => s.parentId != null && Number(s.parentId) === deletedIdNum)
    .forEach((s) => ids.add(Number(s.id)));
  return Array.from(ids);
}

export function compareSodasBySequence(a: Sodo, b: Sodo): number {
  const seqA = a.gameWiseSodaSequence ?? a.id ?? 0;
  const seqB = b.gameWiseSodaSequence ?? b.id ?? 0;
  if (seqA !== seqB) return seqA - seqB;
  return (a.id ?? 0) - (b.id ?? 0);
}

export function sortSodasBySequence(sodas: Sodo[]): Sodo[] {
  if (sodas.length <= 1) return sodas;
  return [...sodas].sort(compareSodasBySequence);
}

export function computeMaxGameWiseSodaSequence(sodas: Sodo[]): number {
  let max = 0;
  for (const s of sodas) {
    const seq = s.gameWiseSodaSequence;
    if (seq != null && Number.isFinite(seq) && seq > max) {
      max = seq;
    }
  }
  return max;
}

/** Insert created rows at the correct sequence position; skip ids that already exist. */
export function mergeSodasCreated(prev: Sodo[], created: Sodo[]): Sodo[] {
  if (created.length === 0) return prev;
  const existingIds = new Set(prev.map((s) => s.id));
  const toAdd = created.filter((s) => !existingIds.has(s.id));
  if (toAdd.length === 0) return prev;
  return sortSodasBySequence([...prev, ...toAdd]);
}

/** Replace rows matching each id in the update batch, preserving sequence order. */
export function mergeSodasUpdated(prev: Sodo[], updated: Sodo[]): Sodo[] {
  if (updated.length === 0) return prev;
  const byId = new Map(updated.map((s) => [s.id, s]));
  return sortSodasBySequence(prev.map((s) => (byId.has(s.id) ? byId.get(s.id)! : s)));
}

/** Remove sodas by id. */
export function removeSodasByIds(prev: Sodo[], sodaIds: number[]): Sodo[] {
  if (sodaIds.length === 0) return prev;
  const removeSet = new Set(sodaIds);
  return prev.filter((s) => !removeSet.has(s.id));
}

/** Upsert a single soda (resync) at the correct sequence position. */
export function upsertSoda(prev: Sodo[], row: Sodo): Sodo[] {
  const idx = prev.findIndex((s) => s.id === row.id);
  if (idx === -1) return sortSodasBySequence([...prev, row]);
  const next = [...prev];
  next[idx] = row;
  return sortSodasBySequence(next);
}

/** Apply a batch of upserts/deletes from resync, then sort once. */
export function applySodaChangeBatch(
  prev: Sodo[],
  upserts: Sodo[],
  deletedSodaIds: number[],
): Sodo[] {
  let next = prev;
  for (const row of upserts) {
    if (row.isDeleted) {
      next = removeSodasByIds(next, [row.id]);
    } else {
      const idx = next.findIndex((s) => s.id === row.id);
      if (idx === -1) next = [...next, row];
      else {
        const copy = [...next];
        copy[idx] = row;
        next = copy;
      }
    }
  }
  next = removeSodasByIds(next, deletedSodaIds);
  return sortSodasBySequence(next);
}
